import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';
import api from '../services/api';

/* ── HospitalCard ─────────────────────────────────────────────────── */
const HospitalCard = ({ hospital, onBook }) => (
  <div
    className="product-card"
    style={{ gap: 14 }}
    onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 20px rgba(79,70,229,0.10)'}
    onMouseLeave={e => e.currentTarget.style.boxShadow = ''}
  >
    <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
      {/* Icon / Logo */}
      <div style={{
        width: 64, height: 64, borderRadius: 14, flexShrink: 0,
        background: 'linear-gradient(135deg, #eef2ff, #e0e7ff)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32,
        border: '1.5px solid #c7d2fe',
      }}>
        {hospital.logoEmoji || '🏥'}
      </div>

      <div style={{ minWidth: 0, flex: 1 }}>
        <div className="t-h4" style={{ fontSize: 15, marginBottom: 3 }}>
          {hospital.name}
        </div>
        {hospital.type && (
          <div style={{
            display: 'inline-block',
            background: '#f5f3ff', color: '#6d28d9',
            padding: '2px 10px', borderRadius: 9999,
            fontWeight: 500, fontSize: 11.5, marginBottom: 6,
          }}>
            {hospital.type}
          </div>
        )}
        <div className="t-body-sm" style={{ marginBottom: 4, color: '#6b7280' }}>
          📍 {hospital.address || 'Address not available'}
        </div>
        {hospital.phone && (
          <div className="t-meta">📞 {hospital.phone}</div>
        )}
      </div>
    </div>

    {/* Specialties */}
    {hospital.specialties && hospital.specialties.length > 0 && (
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
        {hospital.specialties.slice(0, 5).map(s => (
          <span key={s} className="tag">{s}</span>
        ))}
        {hospital.specialties.length > 5 && (
          <span className="tag" style={{ color: '#9ca3af' }}>+{hospital.specialties.length - 5} more</span>
        )}
      </div>
    )}

    {/* Footer */}
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
      <div>
        {hospital.timings && (
          <div className="t-meta">🕐 {hospital.timings}</div>
        )}
        {hospital.distanceKm != null && (
          <div className="t-meta" style={{ marginTop: 2 }}>📏 {hospital.distanceKm} km away</div>
        )}
      </div>
      <button
        className="btn-primary-sm"
        onClick={() => onBook(hospital)}
        style={{ flexShrink: 0 }}
      >
        Book Visit →
      </button>
    </div>
  </div>
);

const SkeletonCard = () => (
  <div className="card flex-col-gap12">
    {[64, 40, 24, 20].map((h, i) => (
      <div key={i} className="skeleton" style={{ height: h, width: i === 1 ? '70%' : '90%', borderRadius: 8 }} />
    ))}
  </div>
);

/* ── Hospitals ────────────────────────────────────────────────────── */
const Hospitals = () => {
  const navigate = useNavigate();
  const { tenant } = useTenant();

  const [hospitals, setHospitals] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [search, setSearch]       = useState('');

  useEffect(() => {
    if (!tenant?.id) return;
    setLoading(true);
    setError(null);
    api.get('/consultations/hospitals')
      .then(({ data }) => setHospitals(Array.isArray(data) ? data : []))
      .catch(() => setError('Could not load hospitals. Please try again.'))
      .finally(() => setLoading(false));
  }, [tenant?.id]);

  const filtered = hospitals.filter(h => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (h.name || '').toLowerCase().includes(q) ||
      (h.address || '').toLowerCase().includes(q) ||
      (h.type || '').toLowerCase().includes(q) ||
      (h.specialties || []).some(s => s.toLowerCase().includes(q))
    );
  });

  const handleBook = (hospital) => {
    navigate(`/services/book-appointment?mode=walk-in&hospitalId=${hospital.id}`);
  };

  if (!tenant) {
    return (
      <div className="page">
        <main className="page-main">
          <div className="breadcrumb">
            <button className="t-link" onClick={() => navigate('/')}>Home</button>
            <span>›</span>
            <button className="t-link" onClick={() => navigate('/services/online-consultation')}>Consult</button>
            <span>›</span>
            <span className="bc-current">Hospitals</span>
          </div>
          <div className="fade-up pharmacy-head">
            <h1 className="t-h1">🏥 Hospitals & Clinics</h1>
            <p className="t-body-sm">Select a tenant from the top bar to see hospitals near you.</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="page">
      <main className="page-main">

        <div className="breadcrumb">
          <button className="t-link" onClick={() => navigate('/')}>Home</button>
          <span>›</span>
          <button className="t-link" onClick={() => navigate('/services/online-consultation')}>Consult</button>
          <span>›</span>
          <span className="bc-current">Hospitals & Clinics</span>
        </div>

        <div className="fade-up pharmacy-head">
          <h1 className="t-h1">🏥 Hospitals & Clinics</h1>
          <p className="t-body-sm">Find hospitals and clinics near you for in-person consultations.</p>
        </div>

        {/* Search bar */}
        <div className="card fade-up-1" style={{ padding: '12px 16px', marginBottom: '1.25rem' }}>
          <div className="search-wrap" style={{ marginBottom: 0 }}>
            <svg className="search-icon" width="16" height="16" fill="none" stroke="#9ca3af" strokeWidth="2" viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" strokeLinecap="round" />
            </svg>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="search-input"
              placeholder="Search by hospital name, specialty, area…"
              style={{ height: 38, fontSize: 13 }}
            />
            {search && (
              <button className="search-clear" onClick={() => setSearch('')} style={{ fontSize: 16 }}>×</button>
            )}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="error-state" style={{ marginBottom: '1rem' }}>
            <span>⚠️</span> {error}
            <button className="t-link" style={{ marginLeft: 12 }} onClick={() => window.location.reload()}>Retry</button>
          </div>
        )}

        {/* Grid */}
        <div
          className="fade-up-2"
          style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}
        >
          {loading
            ? Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)
            : filtered.length === 0
              ? (
                <div className="empty-state" style={{ gridColumn: '1 / -1' }}>
                  <div className="empty-icon">🏥</div>
                  <p className="empty-title">No hospitals found</p>
                  <p className="empty-body">
                    {search ? 'Try a different search term.' : 'No hospitals are listed for this location yet.'}
                  </p>
                </div>
              )
              : filtered.map((h, i) => (
                <div key={h.id || i} style={{ animation: `fadeUp .3s ${i * 30}ms ease both` }}>
                  <HospitalCard hospital={h} onBook={handleBook} />
                </div>
              ))
          }
        </div>

      </main>
    </div>
  );
};

export default Hospitals;